// One status vocabulary for orders and tasks (Zen Design Adoption Plan · Status semantics).
import { formatDuration } from './format';
import type { DepartmentTask, WorkOrder } from './types';

export const LOGISTICS_DEPARTMENT_ID = 'dept-delivery';

export type StatusTone =
  | 'neutral'
  | 'waiting'
  | 'warning'
  | 'info'
  | 'ready'
  | 'out'
  | 'success'
  | 'danger';

export interface StatusInfo {
  label: string;
  tone: StatusTone;
}

export function isLogisticsTask(task: DepartmentTask): boolean {
  return task.departmentId === LOGISTICS_DEPARTMENT_ID;
}

export function orderProgress(order: WorkOrder): { done: number; total: number } {
  const tasks = order.tasks;
  return {
    done: tasks.filter((task) => task.status === 'completed').length,
    total: tasks.length,
  };
}

export function orderStatus(order: WorkOrder): StatusInfo {
  if (order.deletedAt) return { label: 'Deleted', tone: 'danger' };
  if (order.status === 'cancelled') return { label: 'Cancelled', tone: 'danger' };
  if (order.status === 'completed') return { label: 'Completed', tone: 'success' };
  if (!order.accountsApproved) return { label: 'Awaiting invoice', tone: 'warning' };

  const logistics = order.tasks.filter(isLogisticsTask);
  if (logistics.some((task) => task.status === 'started')) {
    return { label: 'Out for delivery', tone: 'out' };
  }
  const production = order.tasks.filter((task) => !isLogisticsTask(task));
  const productionDone = production.length > 0 && production.every((task) => task.status === 'completed');
  if (productionDone && logistics.some((task) => task.status === 'pending')) {
    return { label: 'Ready for delivery', tone: 'ready' };
  }
  const { done, total } = orderProgress(order);
  return { label: `In progress · ${done} of ${total}`, tone: 'info' };
}

export function taskStatus(task: DepartmentTask): StatusInfo {
  if (task.status === 'completed') return { label: 'Done', tone: 'success' };
  if (task.status === 'started') {
    return { label: task.pickedUpByName ? `Started · ${task.pickedUpByName}` : 'Started', tone: 'info' };
  }
  if (!task.availableAt) return { label: 'Waiting', tone: 'waiting' };
  return { label: 'Pending', tone: 'neutral' };
}

/** "Started 40 min ago · Imran" · "Waiting 2 days" · "Done by Zainab" */
export function taskTimeInState(task: DepartmentTask, now = new Date()): string | null {
  if (task.status === 'completed') {
    const by = task.finishedByName ?? task.pickedUpByName;
    return by ? `Done by ${by}` : 'Done';
  }
  if (task.status === 'started' && task.startedAt) {
    const since = formatDuration(now.getTime() - new Date(task.startedAt).getTime());
    const when = since === 'just now' ? 'Started just now' : `Started ${since} ago`;
    return task.pickedUpByName ? `${when} · ${task.pickedUpByName}` : when;
  }
  if (task.status === 'pending' && task.availableAt) {
    const since = formatDuration(now.getTime() - new Date(task.availableAt).getTime());
    return since === 'just now' ? 'Just arrived' : `Waiting ${since}`;
  }
  return null;
}

const DESIGN_DEPARTMENT_ID = 'dept-design';

/** Track order for one item: Design first, then production, then Installation/Delivery. */
export function orderedTrackTasks(tasks: DepartmentTask[]): DepartmentTask[] {
  const rank = (task: DepartmentTask) =>
    task.departmentId === DESIGN_DEPARTMENT_ID ? 0 : isLogisticsTask(task) ? 2 : 1;
  return [...tasks].sort((left, right) => rank(left) - rank(right) || left.departmentName.localeCompare(right.departmentName));
}
